/**
 * Stripe Service
 * Handles communication with Firebase Cloud Functions for Stripe integration
 */

import { auth } from '../firebase';
import { devLog, devWarn, devError } from '../utils/debugLogger';
import { FUNCTION_URLS as CONFIG_URLS } from '../config/functions';

// Cloud Functions URLs from centralized config
const FUNCTION_URLS = CONFIG_URLS.stripe;

/**
 * Get auth headers with Firebase ID token
 * @returns {Promise<Object>} Headers object with Authorization if user is logged in
 */
const getAuthHeaders = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
      devLog('[Stripe] Auth header added for user:', user.uid);
    } catch (error) {
      devWarn('[Stripe] Failed to get auth token:', error.message);
    }
  } else {
    devLog('[Stripe] No authenticated user - request will be unauthenticated');
  }
  
  return headers;
};

/**
 * Create a checkout session (hosted mode - legacy)
 * @param {string} priceType - 'monthly' or 'yearly'
 * @param {string} userId - Firebase user ID
 * @param {string} userEmail - User's email address
 * @param {Object} options - Optional: { promoCode, affiliateCode }
 * @returns {Promise<{url: string, sessionId: string}>}
 */
export const createCheckoutSession = async (priceType, userId, userEmail, options = {}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(FUNCTION_URLS.createCheckoutSession, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        priceType,
        userId,
        userEmail,
        embedded: false,
        promoCode: options.promoCode || null,
        affiliateCode: options.affiliateCode || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    return await response.json();
  } catch (error) {
    devError('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create an embedded checkout session
 * Returns clientSecret for use with EmbeddedCheckout
 * @param {string} priceType - 'monthly' or 'yearly'
 * @param {string} userId - Firebase user ID
 * @param {string} userEmail - User's email address
 * @param {Object} options - Optional: { promoCode, affiliateCode }
 * @returns {Promise<{clientSecret: string, sessionId: string}>}
 */
export const createEmbeddedCheckoutSession = async (priceType, userId, userEmail, options = {}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(FUNCTION_URLS.createCheckoutSession, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        priceType,
        userId,
        userEmail,
        embedded: true,
        promoCode: options.promoCode || null,
        affiliateCode: options.affiliateCode || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create checkout session');
    }

    return await response.json();
  } catch (error) {
    devError('Error creating embedded checkout session:', error);
    throw error;
  }
};

/**
 * Create a customer portal session for subscription management
 * @param {string} userId - Firebase user ID
 * @returns {Promise<{url: string}>}
 */
export const createPortalSession = async (userId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(FUNCTION_URLS.createPortalSession, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    return await response.json();
  } catch (error) {
    devError('Error creating portal session:', error);
    throw error;
  }
};

/**
 * Get subscription status from the server
 * @param {string} userId - Firebase user ID
 * @returns {Promise<{tier: string, subscriptionStatus: string|null, subscriptionEnd: Date|null}>}
 */
export const getSubscriptionStatus = async (userId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(FUNCTION_URLS.getSubscriptionStatus, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get subscription status');
    }

    return await response.json();
  } catch (error) {
    devError('Error getting subscription status:', error);
    throw error;
  }
};

/**
 * Redirect to Stripe Checkout
 * @param {string} priceType - 'monthly' or 'yearly'
 * @param {string} userId - Firebase user ID
 * @param {string} userEmail - User's email address
 */
export const redirectToCheckout = async (priceType, userId, userEmail) => {
  const { url } = await createCheckoutSession(priceType, userId, userEmail);
  window.location.href = url;
};

/**
 * Redirect to Stripe Customer Portal
 * @param {string} userId - Firebase user ID
 */
export const redirectToPortal = async (userId) => {
  const { url } = await createPortalSession(userId);
  window.location.href = url;
};

/**
 * Sync subscription status from Stripe
 * Useful when webhook didn't fire or data is out of sync
 * @param {string} userId - Firebase user ID
 * @returns {Promise<{message: string, subscriptionStatus: string, cancelAtPeriodEnd: boolean, tier: string}>}
 */
export const syncSubscriptionStatus = async (userId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(FUNCTION_URLS.syncSubscriptionStatus, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync subscription status');
    }

    return await response.json();
  } catch (error) {
    devError('Error syncing subscription status:', error);
    throw error;
  }
};

// ============================================
// AFFILIATE & PROMO CODE UTILITIES
// ============================================

const AFFILIATE_STORAGE_KEY = 'mind_affiliate_code';
const PROMO_STORAGE_KEY = 'mind_promo_code';

/**
 * Extract and store affiliate/promo codes from URL
 * Call this on app load (e.g., in App.js or LandingPage)
 * 
 * Supported URL patterns:
 * - https://my-mind.me/?ref=AFFILIATE_CODE
 * - https://my-mind.me/?affiliate=AFFILIATE_CODE
 * - https://my-mind.me/?promo=PROMO_CODE
 * - https://my-mind.me/?ref=AFFILIATE&promo=PROMO
 */
export const captureReferralCodes = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    
    // Affiliate codes (ref or affiliate param)
    const affiliateCode = params.get('ref') || params.get('affiliate');
    if (affiliateCode) {
      localStorage.setItem(AFFILIATE_STORAGE_KEY, affiliateCode);
      devLog('[Stripe] Captured affiliate code:', affiliateCode);
    }
    
    // Promo codes
    const promoCode = params.get('promo') || params.get('coupon');
    if (promoCode) {
      localStorage.setItem(PROMO_STORAGE_KEY, promoCode);
      devLog('[Stripe] Captured promo code:', promoCode);
    }
  } catch (error) {
    devWarn('[Stripe] Error capturing referral codes:', error);
  }
};

/**
 * Get stored affiliate code
 * @returns {string|null}
 */
export const getStoredAffiliateCode = () => {
  try {
    return localStorage.getItem(AFFILIATE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Get stored promo code
 * @returns {string|null}
 */
export const getStoredPromoCode = () => {
  try {
    return localStorage.getItem(PROMO_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Clear stored codes after successful checkout
 */
export const clearStoredCodes = () => {
  try {
    localStorage.removeItem(AFFILIATE_STORAGE_KEY);
    localStorage.removeItem(PROMO_STORAGE_KEY);
    devLog('[Stripe] Cleared stored referral codes');
  } catch {
    // Ignore
  }
};

/**
 * Get checkout options with stored affiliate/promo codes
 * Use this when creating a checkout session
 * @param {Object} overrides - Override stored values
 * @returns {Object} { promoCode, affiliateCode }
 */
export const getCheckoutOptions = (overrides = {}) => {
  return {
    promoCode: overrides.promoCode || getStoredPromoCode(),
    affiliateCode: overrides.affiliateCode || getStoredAffiliateCode(),
  };
};
